import React, { useContext, useEffect, useState } from 'react';
import common from '../../common.module.scss';
import {
    getRooms,
    updateRoom,
    createRoom,
    deleteRoom
} from '../../api/adminApi';
import CreateEntityModal from '../CreateEntityModal.jsx';
import ConfirmDeleteModal from '../ConfirmDeleteModal.jsx';
import { toast } from "react-toastify";
import Pagination from '../pagination';
import { EmployeeContext } from "../../context/EmployeeContext.jsx";
import useIsMobile from "@/shared/lib/hooks/useIsMobile";
import { FaCheck } from "react-icons/fa6";
import { ImCross } from "react-icons/im";
import { GoPencil } from "react-icons/go";
import { RiDeleteBinLine } from "react-icons/ri";
import { GiSettingsKnobs } from "react-icons/gi";
import FilterModal from "@/shared/ui/FiltersModal";

export default function RoomTable() {
    const { employee } = useContext(EmployeeContext);
    const role = employee?.role;

    const [rooms, setRooms] = useState([]);
    const [, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [editRoomId, setEditRoomId] = useState(null);
    const [editedName, setEditedName] = useState('');
    const [editedCyberpunk, setEditedCyberpunk] = useState(false);
    const [editedUsedInPrivateRequests, setEditedUsedInPrivateRequests] = useState(false);
    const [saving, setSaving] = useState(false);

    const [showModal, setShowModal] = useState(false);
    const [roomToDelete, setRoomToDelete] = useState(null);

    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState('');
    const [direction, setDirection] = useState('asc');
    const [showModalFilters, setShowModalFilters] = useState(false);

    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    const isMobile = useIsMobile();

    useEffect(() => {
        loadRooms();
    }, [page, search, sortBy, direction]);

    const loadRooms = async () => {
        try {
            setLoading(true);

            const response = await getRooms({
                page,
                size: 10,
                ...(search && { search }),
                ...(sortBy && { sortBy }),
                ...(sortBy && { direction }),
            });

            const fetchedRooms = response.data.content || response.data;

            setRooms(
                fetchedRooms.map(room => ({
                    id: room.id,
                    name: room.name,
                    enabled: room.isActive,
                    isCyberpunkRoom: !!room.isCyberpunkRoom,
                    isUsedInPrivateRequests: !!room.isUsedInPrivateRequests,
                }))
            );

            setTotalPages(response.data.totalPages || 1);
        } catch (e) {
            const errorMessage = e?.response?.data?.message || 'Ошибка при загрузке залов';
            toast.error(errorMessage);
            setError('Ошибка при загрузке залов');
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (field) => {
        if (sortBy !== field) {
            setSortBy(field);
            setDirection('desc');
        } else if (direction === 'desc') {
            setDirection('asc');
        } else {
            setSortBy('');
            setDirection('asc');
        }
        setPage(0);
    };

    const renderSortArrow = (field) => {
        if (sortBy !== field) return '';
        return direction === 'asc' ? ' ↑' : ' ↓';
    };

    const toggleSwitch = async (id) => {
        const updatedRoom = rooms.find(r => r.id === id);
        if (!updatedRoom) return;

        const newStatus = !updatedRoom.enabled;

        try {
            await updateRoom(id, {
                name: updatedRoom.name,
                isActive: newStatus,
                isCyberpunkRoom: updatedRoom.isCyberpunkRoom,
                isUsedInPrivateRequests: updatedRoom.isUsedInPrivateRequests,
            });

            setRooms(prev =>
                prev.map(room =>
                    room.id === id ? { ...room, enabled: newStatus } : room
                )
            );
        } catch (e) {
            const errorMessage = e?.response?.data?.message || 'Ошибка при обновлении зала';
            toast.error(errorMessage);
        }
    };

    const handleEdit = (room) => {
        setEditRoomId(room.id);
        setEditedName(room.name);
        setEditedCyberpunk(room.isCyberpunkRoom);
        setEditedUsedInPrivateRequests(room.isUsedInPrivateRequests);
    };

    const handleCancel = () => {
        setEditRoomId(null);
        setEditedName('');
        setEditedCyberpunk(false);
        setEditedUsedInPrivateRequests(false);
    };

    const handleSave = async (room) => {
        if (editedName.trim() === '') {
            toast.error('Название не может быть пустым');
            return;
        }

        setSaving(true);
        try {
            await updateRoom(room.id, {
                name: editedName,
                isActive: room.enabled,
                isCyberpunkRoom: editedCyberpunk,
                isUsedInPrivateRequests: editedUsedInPrivateRequests,
            });

            setRooms(prev =>
                prev.map(r =>
                    r.id === room.id
                        ? {
                            ...r,
                            name: editedName,
                            isCyberpunkRoom: editedCyberpunk,
                            isUsedInPrivateRequests: editedUsedInPrivateRequests,
                        }
                        : r
                )
            );

            handleCancel();
        } catch (e) {
            const errorMessage = e?.response?.data?.message || 'Ошибка при сохранении изменений';
            toast.error(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    const handleCreateRoom = async (name, checkboxValues = {}) => {
        try {
            const response = await createRoom({
                name,
                isActive: true,
                isCyberpunkRoom: !!checkboxValues.isCyberpunkRoom,
                isUsedInPrivateRequests: !!checkboxValues.isUsedInPrivateRequests,
            });

            setRooms(prev => [
                ...prev,
                {
                    id: response.data.id,
                    name: response.data.name,
                    enabled: response.data.isActive,
                    isCyberpunkRoom: !!response.data.isCyberpunkRoom,
                    isUsedInPrivateRequests: !!response.data.isUsedInPrivateRequests,
                }
            ]);
        } catch (e) {
            const errorMessage = e?.response?.data?.message || 'Ошибка при создании зала';
            toast.error(errorMessage);
        }
    };

    const confirmDelete = async () => {
        if (!roomToDelete) return;

        try {
            await deleteRoom(roomToDelete.id);
            setRooms(prev => prev.filter(r => r.id !== roomToDelete.id));
        } catch (e) {
            const errorMessage = e?.response?.data?.message || 'Ошибка при удалении зала';
            toast.error(errorMessage);
        } finally {
            setRoomToDelete(null);
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < totalPages) {
            setPage(newPage);
        }
    };

    const handleChangeSort = (field, dir) => {
        setSortBy(field);
        setDirection(dir);
        setPage(0);
    };

    const sortOptions = [
        { value: 'name', label: 'Название' },
        { value: 'isCyberpunkRoom', label: 'Cyberpunk' },
        { value: 'isUsedInPrivateRequests', label: 'Приватные заявки' },
        { value: 'isActive', label: 'Состояние' },
    ];

    if (error) return <h2>{error}</h2>;

    return (
        <div className={`container-fluid ${common.container}`}>
            {isMobile && <h1 className={common.mobileHeading}>Залы</h1>}

            <div className="d-flex align-items-center mb-3">
                <div className="col-md-5 col-10">
                    <input
                        type="text"
                        placeholder="Поиск"
                        className={`form-control w-100 ${common.searchInput}`}
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(0);
                        }}
                    />
                </div>

                {isMobile && (
                    <div className="col-2 d-flex justify-content-end">
                        <button
                            type="button"
                            className={`${common.iconButton} ms-2`}
                            onClick={() => setShowModalFilters(true)}
                            aria-label="Открыть сортировку"
                        >
                            <GiSettingsKnobs size={20} />
                        </button>
                    </div>
                )}

                {role !== "ADMIN" && role !== "MASTER" && !isMobile && (
                    <div className="ms-3">
                        <button
                            className="service-btn"
                            onClick={() => setShowModal(true)}
                        >
                            Добавить зал
                        </button>
                    </div>
                )}
            </div>

            <table className={`table ${common.table}`}>
                <thead className={common.tableHeader}>
                <tr>
                    <th className={common.sortable} onClick={() => handleSort('name')}>
                        Название зала{renderSortArrow('name')}
                    </th>
                    <th className={common.sortable} onClick={() => handleSort('isCyberpunkRoom')}>
                        Cyberpunk{renderSortArrow('isCyberpunkRoom')}
                    </th>
                    <th className={common.sortable} onClick={() => handleSort('isUsedInPrivateRequests')}>
                        В приватных заявках{renderSortArrow('isUsedInPrivateRequests')}
                    </th>
                    <th className={common.sortable} onClick={() => handleSort('isActive')}>
                        Состояние{renderSortArrow('isActive')}
                    </th>
                </tr>
                </thead>

                <tbody>
                {rooms.map((room, index) => (
                    <tr key={room.id} className={index % 2 === 1 ? common.altRow : ''}>
                        <td className="align-middle">
                            {editRoomId === room.id ? (
                                <input
                                    className="form-control"
                                    value={editedName}
                                    onChange={(e) => setEditedName(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleSave(room);
                                        }
                                        if (e.key === 'Escape') {
                                            handleCancel();
                                        }
                                    }}
                                    autoFocus
                                />
                            ) : (
                                room.name
                            )}
                        </td>

                        <td className="align-middle">
                            {editRoomId === room.id ? (
                                <div className="form-check form-switch m-0">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={editedCyberpunk}
                                        onChange={(e) => setEditedCyberpunk(e.target.checked)}
                                    />
                                </div>
                            ) : (
                                room.isCyberpunkRoom ? 'Да' : 'Нет'
                            )}
                        </td>

                        <td className="align-middle">
                            {editRoomId === room.id ? (
                                <div className="form-check form-switch m-0">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={editedUsedInPrivateRequests}
                                        onChange={(e) => setEditedUsedInPrivateRequests(e.target.checked)}
                                    />
                                </div>
                            ) : (
                                room.isUsedInPrivateRequests ? 'Да' : 'Нет'
                            )}
                        </td>

                        <td className="align-middle">
                            <div className="d-flex align-items-center justify-content-between">
                                <div className="form-check form-switch me-3">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={room.enabled}
                                        onChange={() => toggleSwitch(room.id)}
                                    />
                                </div>

                                {editRoomId === room.id ? (
                                    <>
                                        <button
                                            className={`btn-word black btn-link p-0 me-2 ${common.editBtn}`}
                                            onClick={() => handleSave(room)}
                                            disabled={saving}
                                        >
                                            {saving
                                                ? 'Сохранение...'
                                                : isMobile
                                                    ? <FaCheck className={common.icon} />
                                                    : 'Сохранить'}
                                        </button>
                                        <button
                                            className={`btn-word black btn-link text-danger p-0 me-2 ${common.editBtn}`}
                                            onClick={handleCancel}
                                            disabled={saving}
                                        >
                                            {isMobile
                                                ? <ImCross className={common.icon} />
                                                : 'Отменить'}
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        {role !== "ADMIN" && role !== "MASTER" && (
                                            <>
                                                <button
                                                    className={`btn-word black btn-link p-0 me-2 ${common.editBtn}`}
                                                    onClick={() => handleEdit(room)}
                                                >
                                                    {isMobile
                                                        ? <GoPencil className={common.icon} />
                                                        : 'Редактировать'}
                                                </button>
                                                <button
                                                    className={`btn-word red btn-link text-danger p-0 ${common.editBtn}`}
                                                    onClick={() => setRoomToDelete(room)}
                                                >
                                                    {isMobile
                                                        ? <RiDeleteBinLine className={common.icon} />
                                                        : 'Удалить'}
                                                </button>
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>

            <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
            />

            {role !== "ADMIN" && role !== "MASTER" && isMobile && (
                <div>
                    <button
                        className="service-btn w-100"
                        onClick={() => setShowModal(true)}
                    >
                        Добавить зал
                    </button>
                </div>
            )}

            {showModal && (
                <CreateEntityModal
                    title="Добавление нового зала"
                    placeholder="Введите название зала"
                    onClose={() => setShowModal(false)}
                    onSave={handleCreateRoom}
                    checkboxes={[
                        {
                            name: 'isCyberpunkRoom',
                            label: 'Cyberpunk зал',
                            defaultValue: false,
                        },
                        {
                            name: 'isUsedInPrivateRequests',
                            label: 'Использовать в приватных заявках',
                            defaultValue: true,
                        },
                    ]}
                />
            )}

            {roomToDelete && (
                <ConfirmDeleteModal
                    roomName={roomToDelete.name}
                    onConfirm={confirmDelete}
                    onCancel={() => setRoomToDelete(null)}
                />
            )}

            <FilterModal
                isOpen={showModalFilters}
                onClose={() => setShowModalFilters(false)}
                sortOptions={sortOptions}
                sortBy={sortBy}
                direction={direction}
                onChangeSort={handleChangeSort}
            />
        </div>
    );
}